import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Modal, Form, Alert, Badge, Row, Col } from 'react-bootstrap';
import { getAuthenticatedAxios } from './axiosConfig';
import { useAuth } from '../AuthContext/AuthContext';
import { BASE_URL } from '../../Constants/constants';
import { formatDate, formatDateTimeFull, formatDateOnly } from './Dateutils';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const HRCheckoutDashboard = () => {
    const { t } = useTranslation('hrCheckoutDashboard');
    const { currentUser } = useAuth();
    const [pendingCheckouts, setPendingCheckouts] = useState([]);
    const [selectedCheckout, setSelectedCheckout] = useState(null);
    const [showProcessModal, setShowProcessModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const [departmentalClearance, setDepartmentalClearance] = useState(false);

    const getOptionalRequirements = () => [
        { id: 'documents', label: t('requirements.documents'), checked: false },
        { id: 'interview', label: t('requirements.interview'), checked: false },
        { id: 'finances', label: t('requirements.finances'), checked: false }
    ];

    const [optionalRequirements, setOptionalRequirements] = useState(getOptionalRequirements());
    const [notes, setNotes] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);

    const fetchPendingCheckouts = async () => {
        try {
            setLoading(true);
            const axios = getAuthenticatedAxios();
            const response = await axios.get(`${BASE_URL}/checkout/pending-hr`);

            const checkouts = Array.isArray(response.data) ? response.data : [];
            const validCheckouts = checkouts.filter(checkout =>
                checkout &&
                checkout.user_id &&
                (checkout.first_name || checkout.last_name)
            );

            setPendingCheckouts(validCheckouts);
            setError(null);
        } catch (err) {
            console.error('Error fetching pending checkouts:', err);
            setError(err.response?.data?.detail || t('errors.fetch_failed'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!currentUser) {
            setError(t('errors.auth_required'));
            setLoading(false);
            return;
        }
        fetchPendingCheckouts();
        const pollingInterval = setInterval(fetchPendingCheckouts, 30000);
        return () => clearInterval(pollingInterval);
    }, [currentUser]);

    const handleProcessCheckout = (checkout) => {
        setSelectedCheckout(checkout);
        setDepartmentalClearance(false);
        setOptionalRequirements(getOptionalRequirements());
        setNotes('');
        setShowProcessModal(true);
    };

    const updateOptionalRequirement = (id, checked) => {
        setOptionalRequirements(optionalRequirements.map(req =>
            req.id === id ? { ...req, checked } : req
        ));
    };

    const canCompleteCheckout = () => departmentalClearance;

    const getOptionalCompletedCount = () =>
        optionalRequirements.filter(req => req.checked).length;

    const handleSubmitProcess = async () => {
        try {
            setIsProcessing(true);
            const authenticatedAxios = getAuthenticatedAxios();

            const processData = {
                user_id: selectedCheckout.user_id,
                notes: notes || t('process.default_notes'),
                // FIXED: schema field is exit_interview_completed not departmental_clearance_granted
                exit_interview_completed: departmentalClearance,
                checklist: [
                    { id: 'clearance', label: t('requirements.clearance'), status: departmentalClearance },
                    ...optionalRequirements.map(req => ({ id: req.id, label: req.label, status: req.checked }))
                ]
            };

            // FIXED: endpoint is /checkout/hr-finalize not /checkout/hr-process
            const response = await authenticatedAxios.post(
                `${BASE_URL}/checkout/hr-finalize`,
                processData
            );

            if (response.data) {
                // ADDED: send HR completion email to staff + HR department
                // This is the final stage — staff email confirms full checkout completion
                // and that all documents have been submitted
                try {
                    await axios.post(`${BASE_URL}/notify/hr-completed`, {
                        user_id: selectedCheckout.user_id,
                        notes: notes || null
                    });
                } catch (emailErr) {
                    // Email failure should not block the checkout flow
                    console.warn('HR completion email failed:', emailErr);
                }

                setShowProcessModal(false);
                setShowConfirmModal(false);
                setSelectedCheckout(null);
                fetchPendingCheckouts();
                setError(null);
            }
        } catch (err) {
            console.error('HR Processing Error:', err);
            setError(err.response?.data?.detail || t('errors.process_failed'));
        } finally {
            setIsProcessing(false);
        }
    };

    const getEmployeeName = (checkout) => {
        if (!checkout) return t('table.unknown');
        return `${checkout.first_name || ''} ${checkout.last_name || ''}`.trim() || t('table.unknown');
    };

    return (
        <Container fluid className="mt-4">
            <Card>
                <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <h2>{t('heading')}</h2>
                        <Button variant="outline-primary" onClick={fetchPendingCheckouts} disabled={loading}>
                            <i className="bi bi-arrow-clockwise me-2"></i>
                            {loading ? t('buttons.refreshing') : t('buttons.refresh')}
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body>
                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>
                    )}

                    {loading && pendingCheckouts.length === 0 ? (
                        <Alert variant="info">
                            <div className="d-flex align-items-center">
                                <div className="spinner-border spinner-border-sm me-2" role="status">
                                    <span className="visually-hidden">{t('loading.text')}</span>
                                </div>
                                {t('loading.message')}
                            </div>
                        </Alert>
                    ) : pendingCheckouts.length === 0 ? (
                        <Alert variant="success">
                            <i className="bi bi-check-circle me-2"></i>
                            {t('no_pending')}
                        </Alert>
                    ) : (
                        <>
                            <div className="mb-3">
                                <Row>
                                    <Col md={4}>
                                        <Card className="border-success">
                                            <Card.Body>
                                                <h6 className="text-muted">{t('stats.pending')}</h6>
                                                <h2 className="mb-0">{pendingCheckouts.length}</h2>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                            </div>

                            <Table striped bordered hover responsive>
                                <thead>
                                <tr>
                                    <th>{t('table.headers.employee')}</th>
                                    <th>{t('table.headers.email')}</th>
                                    <th>{t('table.headers.ict_status')}</th>
                                    <th>{t('table.headers.security_status')}</th>
                                    <th>{t('table.headers.initiated')}</th>
                                    <th>{t('table.headers.actions')}</th>
                                </tr>
                                </thead>
                                <tbody>
                                {pendingCheckouts.map((checkout) => (
                                    <tr key={checkout.user_id}>
                                        <td>{getEmployeeName(checkout)}</td>
                                        <td>{checkout.email || t('table.no_email')}</td>
                                        <td>
                                            <Badge bg={checkout.ict_checkout_status ? 'success' : 'warning'}>
                                                {checkout.ict_checkout_status ? t('status.completed') : t('status.pending')}
                                            </Badge>
                                        </td>
                                        <td>
                                            <Badge bg={checkout.security_checkout_status ? 'success' : 'warning'}>
                                                {checkout.security_checkout_status ? t('status.completed') : t('status.pending')}
                                            </Badge>
                                        </td>
                                        <td>{formatDate(checkout.created_at)}</td>
                                        <td>
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={() => handleProcessCheckout(checkout)}
                                            >
                                                {t('buttons.process')}
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* Process Modal */}
            <Modal show={showProcessModal} onHide={() => setShowProcessModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{t('modal.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedCheckout && (
                        <>
                            <Card className="mb-3">
                                <Card.Body>
                                    <h5>{t('modal.employee_info')}</h5>
                                    <p className="mb-1"><strong>{t('modal.name')}</strong> {getEmployeeName(selectedCheckout)}</p>
                                    <p className="mb-1"><strong>{t('modal.email')}</strong> {selectedCheckout.email}</p>
                                    <p className="mb-0"><strong>{t('modal.initiated')}</strong> {formatDate(selectedCheckout.created_at)}</p>
                                </Card.Body>
                            </Card>

                            <Card className="mb-3 border-danger">
                                <Card.Header className="bg-danger text-white">
                                    <h6 className="mb-0">{t('requirements.required_title')}</h6>
                                </Card.Header>
                                <Card.Body>
                                    <Form.Group className="mb-0">
                                        <Form.Check
                                            type="checkbox"
                                            id="departmental-clearance"
                                            label={t('requirements.clearance')}
                                            checked={departmentalClearance}
                                            onChange={(e) => setDepartmentalClearance(e.target.checked)}
                                        />
                                        <Form.Text className="text-muted">{t('requirements.clearance_note')}</Form.Text>
                                    </Form.Group>
                                </Card.Body>
                            </Card>

                            <Card className="mb-3">
                                <Card.Header>
                                    <h6 className="mb-0">
                                        {t('requirements.optional_title')} ({getOptionalCompletedCount()}/{optionalRequirements.length})
                                    </h6>
                                </Card.Header>
                                <Card.Body>
                                    <p className="text-muted mb-3">{t('requirements.optional_note')}</p>
                                    {optionalRequirements.map(req => (
                                        <Form.Group key={req.id} className="mb-2">
                                            <Form.Check
                                                type="checkbox"
                                                id={`requirement-${req.id}`}
                                                label={req.label}
                                                checked={req.checked}
                                                onChange={(e) => updateOptionalRequirement(req.id, e.target.checked)}
                                            />
                                        </Form.Group>
                                    ))}
                                </Card.Body>
                            </Card>

                            <Form.Group>
                                <Form.Label>{t('form.notes_label')}</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    placeholder={t('form.notes_placeholder')}
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowProcessModal(false)}>{t('buttons.cancel')}</Button>
                    <Button variant="success" onClick={() => setShowConfirmModal(true)} disabled={!canCompleteCheckout()}>
                        {t('buttons.complete')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Confirm Modal */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('confirmation.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedCheckout && (
                        <>
                            <Alert variant="warning"><strong>{t('confirmation.warning')}</strong></Alert>
                            <div className="mb-3">
                                <h6>{t('confirmation.summary')}</h6>
                                <ul>
                                    <li><strong>{t('confirmation.employee')}</strong> {getEmployeeName(selectedCheckout)}</li>
                                    <li><strong>{t('requirements.clearance')}</strong> {departmentalClearance ? t('confirmation.granted') : t('confirmation.not_granted')}</li>
                                    <li><strong>{t('confirmation.optional_completed')}</strong> {getOptionalCompletedCount()}/{optionalRequirements.length}</li>
                                    {notes && <li><strong>{t('confirmation.notes')}</strong> {t('confirmation.notes_provided')}</li>}
                                </ul>
                            </div>
                            {!departmentalClearance && (
                                <Alert variant="danger">
                                    <small>{t('confirmation.clearance_warning')}</small>
                                </Alert>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>{t('buttons.back')}</Button>
                    <Button variant="primary" onClick={handleSubmitProcess} disabled={isProcessing}>
                        {isProcessing ? t('buttons.processing') : t('buttons.confirm')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default HRCheckoutDashboard;